import * as React from "react";

import type { VariantProps } from "class-variance-authority";

import { Slot } from "@radix-ui/react-slot";
import { cva } from "class-variance-authority";
import { PanelLeft } from "lucide-react";

import { useIsMobile } from "../../hooks";
import { cn } from "../../utils";
import { Button } from "./button";
import { Input } from "./input";
import { Separator } from "./separator";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "./sheet";
import { Skeleton } from "./skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "./tooltip";

const SIDEBAR_COOKIE_NAME = "sidebar_state";
const SIDEBAR_COOKIE_MAX_AGE = 60 * 60 * 24 * 7;
const SIDEBAR_WIDTH = "16rem";
const SIDEBAR_WIDTH_MOBILE = "18rem";
const SIDEBAR_WIDTH_ICON = "3rem";
const SIDEBAR_KEYBOARD_SHORTCUT = "b";

interface SidebarContextProps {
  state: "expanded" | "collapsed";
  open: boolean;
  setOpen: (open: boolean) => void;
  openMobile: boolean;
  setOpenMobile: (open: boolean) => void;
  isMobile: boolean;
  toggleSidebar: () => void;
}

const SidebarContext = React.createContext<SidebarContextProps | null>(null);

function useSidebar() {
  const context = React.useContext(SidebarContext);
  if (!context) {
    throw new Error("useSidebar must be used within a SidebarProvider.");
  }

  return context;
}

function SidebarProvider({
  defaultOpen = true,
  open: openProp,
  onOpenChange: setOpenProp,
  children,
  ...props
}: React.ComponentProps<"div"> & {
  defaultOpen?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}) {
  const isMobile = useIsMobile();
  const [openMobile, setOpenMobile] = React.useState(false);

  // This is the internal state of the sidebar.
  // We use openProp and setOpenProp for control from outside the component.
  const [_open, _setOpen] = React.useState(defaultOpen);
  const open = openProp ?? _open;
  const setOpen = React.useCallback(
    (value: boolean | ((value: boolean) => boolean)) => {
      const openState = typeof value === "function" ? value(open) : value;
      if (setOpenProp) {
        setOpenProp(openState);
      } else {
        _setOpen(openState);
      }

      // This sets the cookie to keep the sidebar state.
      document.cookie = `${SIDEBAR_COOKIE_NAME}=${openState}; path=/; max-age=${SIDEBAR_COOKIE_MAX_AGE}`;
    },
    [setOpenProp, open],
  );

  // Helper to toggle the sidebar.
  const toggleSidebar = React.useCallback(() => {
    return isMobile ? setOpenMobile((open) => !open) : setOpen((open) => !open);
  }, [isMobile, setOpen, setOpenMobile]);

  // Adds a keyboard shortcut to toggle the sidebar.
  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (
        event.key === SIDEBAR_KEYBOARD_SHORTCUT &&
        (event.metaKey || event.ctrlKey)
      ) {
        event.preventDefault();
        toggleSidebar();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [toggleSidebar]);

  // We add a state so that we can do data-state="expanded" or "collapsed".
  // This makes it easier to style the sidebar with Tailwind classes.
  const state = open ? "expanded" : "collapsed";

  const contextValue = React.useMemo<SidebarContextProps>(
    () => ({
      state,
      open,
      setOpen,
      isMobile,
      openMobile,
      setOpenMobile,
      toggleSidebar,
    }),
    [state, open, setOpen, isMobile, openMobile, setOpenMobile, toggleSidebar],
  );

  return (
    <SidebarContext value={contextValue}>
      <TooltipProvider delayDuration={0}>
        <SidebarWrapper {...props}>{children}</SidebarWrapper>
      </TooltipProvider>
    </SidebarContext>
  );
}
SidebarProvider.displayName = "SidebarProvider";

const SidebarWrapper = ({
  className,
  style,
  children,
  ref,
  ...props
}: React.ComponentProps<"div">) => {
  return (
    <div
      ref={ref}
      data-slot="sidebar-wrapper"
      style={
        {
          "--interface-width": SIDEBAR_WIDTH,
          "--interface-width-icon": SIDEBAR_WIDTH_ICON,
          ...style,
        } as React.CSSProperties
      }
      className={cn(
        "af-group/sidebar-wrapper af-flex af-h-full af-w-full has-[[data-variant=inset]]:af-bg-interface",
        className,
      )}
      {...props}
    >
      {children}
    </div>
  );
};
SidebarWrapper.displayName = "SidebarWrapper";

const Sidebar = ({
  ref,
  side = "left",
  variant = "sidebar",
  collapsible = "offcanvas",
  className,
  children,
  ...props
}: React.ComponentProps<"div"> & {
  side?: "left" | "right";
  variant?: "sidebar" | "floating" | "inset";
  collapsible?: "offcanvas" | "icon" | "none";
}) => {
  const { isMobile, state, openMobile, setOpenMobile } = useSidebar();

  if (collapsible === "none") {
    return (
      <div
        data-slot="sidebar"
        className={cn(
          "af-flex af-h-full af-w-[--interface-width] af-flex-col af-bg-interface-background af-text-interface-foreground",
          className,
        )}
        ref={ref}
        {...props}
      >
        {children}
      </div>
    );
  }

  if (isMobile) {
    return (
      <Sheet open={openMobile} onOpenChange={setOpenMobile} {...props}>
        <SheetContent
          data-sidebar="sidebar"
          data-slot="sidebar"
          data-mobile="true"
          className="af-w-[--interface-width] af-bg-interface af-p-0 af-text-interface-foreground [&>button]:af-hidden"
          style={
            {
              "--interface-width": SIDEBAR_WIDTH_MOBILE,
            } as React.CSSProperties
          }
          side={side}
        >
          <SheetHeader className="af-sr-only">
            <SheetTitle>Sidebar</SheetTitle>
            <SheetDescription>Displays the mobile sidebar.</SheetDescription>
          </SheetHeader>
          <div className="af-flex af-h-full af-w-full af-flex-col">
            {children}
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <div
      ref={ref}
      className="af-group af-peer af-hidden af-text-interface-foreground md:af-block"
      data-state={state}
      data-collapsible={state === "collapsed" ? collapsible : ""}
      data-variant={variant}
      data-side={side}
      data-slot="sidebar"
    >
      {/* This is what handles the sidebar gap on desktop */}
      <div
        data-slot="sidebar-gap"
        className={cn(
          "af-relative af-w-[--interface-width] af-bg-transparent af-transition-[width] af-duration-200 af-ease-linear",
          "group-data-[collapsible=offcanvas]:af-w-0",
          "group-data-[side=right]:af-rotate-180",
          variant === "floating" || variant === "inset"
            ? "group-data-[collapsible=icon]:af-w-[calc(var(--interface-width-icon)_+_theme(spacing.4))]"
            : "group-data-[collapsible=icon]:af-w-[--interface-width-icon]",
        )}
      />
      <div
        data-slot="sidebar-container"
        className={cn(
          "af-fixed af-inset-y-0 af-z-10 af-hidden af-h-svh af-w-[--interface-width] af-border-interface-border af-transition-[left,right,width] af-duration-200 af-ease-linear md:af-flex",
          side === "left"
            ? "af-left-0 group-data-[collapsible=offcanvas]:af-left-[calc(var(--interface-width)*-1)]"
            : "af-right-0 group-data-[collapsible=offcanvas]:af-right-[calc(var(--interface-width)*-1)]",
          // Adjust the padding for floating and inset variants.
          variant === "floating" || variant === "inset"
            ? "af-p-2 group-data-[collapsible=icon]:af-w-[calc(var(--interface-width-icon)_+_theme(spacing.4)_+2px)]"
            : "group-data-[collapsible=icon]:af-w-[--interface-width-icon] group-data-[side=left]:af-border-r group-data-[side=right]:af-border-l",
          className,
        )}
        {...props}
      >
        <div
          data-sidebar="sidebar"
          data-slot="sidebar-inner"
          className="af-flex af-h-full af-w-full af-flex-col af-bg-interface-background group-data-[variant=floating]:af-rounded-lg group-data-[variant=floating]:af-border group-data-[variant=floating]:af-border-interface-border group-data-[variant=floating]:af-shadow"
        >
          {children}
        </div>
      </div>
    </div>
  );
};
Sidebar.displayName = "Sidebar";

const SidebarTrigger = ({
  className,
  onClick,
  ...props
}: React.ComponentProps<typeof Button>) => {
  const { toggleSidebar } = useSidebar();

  return (
    <Button
      data-sidebar="trigger"
      data-slot="sidebar-trigger"
      variant="ghost"
      size="icon"
      className={cn("af-h-7 af-w-7", className)}
      onClick={(event) => {
        onClick?.(event);
        toggleSidebar();
      }}
      {...props}
    >
      <PanelLeft />
      <span className="af-sr-only">Toggle Sidebar</span>
    </Button>
  );
};
SidebarTrigger.displayName = "SidebarTrigger";

const SidebarRail = ({
  className,
  ...props
}: React.ComponentProps<"button">) => {
  const { toggleSidebar } = useSidebar();

  return (
    <button
      data-sidebar="rail"
      data-slot="sidebar-rail"
      aria-label="Toggle Sidebar"
      tabIndex={-1}
      onClick={toggleSidebar}
      title="Toggle Sidebar"
      className={cn(
        "af-absolute af-inset-y-0 af-z-20 af-hidden af-w-4 af--translate-x-1/2 af-transition-all af-ease-linear after:af-absolute after:af-inset-y-0 after:af-left-1/2 after:af-w-[2px] hover:after:af-bg-interface-border group-data-[side=left]:af--right-4 group-data-[side=right]:af-left-0 sm:af-flex",
        "[[data-side=left]_&]:af-cursor-w-resize [[data-side=right]_&]:af-cursor-e-resize",
        "[[data-side=left][data-state=collapsed]_&]:af-cursor-e-resize [[data-side=right][data-state=collapsed]_&]:af-cursor-w-resize",
        "group-data-[collapsible=offcanvas]:af-translate-x-0 group-data-[collapsible=offcanvas]:after:af-left-full group-data-[collapsible=offcanvas]:hover:af-bg-interface",
        "[[data-side=left][data-collapsible=offcanvas]_&]:af--right-2",
        "[[data-side=right][data-collapsible=offcanvas]_&]:af--left-2",
        className,
      )}
      {...props}
    />
  );
};
SidebarRail.displayName = "SidebarRail";

const SidebarInset = ({
  className,
  ...props
}: React.ComponentProps<"main">) => {
  return (
    <main
      data-slot="sidebar-inset"
      className={cn(
        "af-relative af-flex af-w-full af-flex-1 af-flex-col af-bg-background",
        "md:peer-data-[variant=inset]:af-m-2 md:peer-data-[state=collapsed]:peer-data-[variant=inset]:af-ml-2 md:peer-data-[variant=inset]:af-ml-0 md:peer-data-[variant=inset]:af-rounded-xl md:peer-data-[variant=inset]:af-shadow",
        className,
      )}
      {...props}
    />
  );
};
SidebarInset.displayName = "SidebarInset";

const SidebarInput = ({
  className,
  ...props
}: React.ComponentProps<typeof Input>) => {
  return (
    <Input
      data-slot="sidebar-input"
      data-sidebar="input"
      className={cn(
        "af-h-8 af-w-full af-bg-background af-shadow-none focus-visible:af-ring-2 focus-visible:af-ring-interface-ring",
        className,
      )}
      {...props}
    />
  );
};
SidebarInput.displayName = "SidebarInput";

const SidebarHeader = ({
  className,
  ...props
}: React.ComponentProps<"div">) => {
  return (
    <div
      data-slot="sidebar-header"
      data-sidebar="header"
      className={cn(
        "af-flex af-flex-col af-gap-2 af-p-2 group-data-[side=right]:af-pr-0",
        className,
      )}
      {...props}
    />
  );
};
SidebarHeader.displayName = "SidebarHeader";

const SidebarFooter = ({
  className,
  ...props
}: React.ComponentProps<"div">) => {
  return (
    <div
      data-slot="sidebar-footer"
      data-sidebar="footer"
      className={cn("af-flex af-flex-col af-gap-2 af-p-2", className)}
      {...props}
    />
  );
};
SidebarFooter.displayName = "SidebarFooter";

const SidebarSeparator = ({
  className,
  ...props
}: React.ComponentProps<typeof Separator>) => {
  return (
    <Separator
      data-slot="sidebar-separator"
      data-sidebar="separator"
      className={cn("af-mx-2 af-w-auto af-bg-interface-border", className)}
      {...props}
    />
  );
};
SidebarSeparator.displayName = "SidebarSeparator";

const SidebarContent = ({
  className,
  ...props
}: React.ComponentProps<"div">) => {
  return (
    <div
      data-slot="sidebar-content"
      data-sidebar="content"
      className={cn(
        "af-flex af-min-h-0 af-flex-1 af-flex-col af-gap-2 af-overflow-auto group-data-[collapsible=icon]:af-overflow-hidden",
        className,
      )}
      {...props}
    />
  );
};
SidebarContent.displayName = "SidebarContent";

const SidebarGroup = ({ className, ...props }: React.ComponentProps<"div">) => {
  return (
    <div
      data-slot="sidebar-group"
      data-sidebar="group"
      className={cn(
        "af-relative af-flex af-w-full af-min-w-0 af-flex-col af-p-2",
        className,
      )}
      {...props}
    />
  );
};
SidebarGroup.displayName = "SidebarGroup";

const SidebarGroupLabel = ({
  className,
  asChild = false,
  ...props
}: React.ComponentProps<"div"> & { asChild?: boolean }) => {
  const Comp = asChild ? Slot : "div";

  return (
    <Comp
      data-slot="sidebar-group-label"
      data-sidebar="group-label"
      className={cn(
        "af-flex af-h-8 af-shrink-0 af-items-center af-rounded-md af-px-2 af-text-xs af-font-medium af-text-interface-foreground/70 af-outline-none af-ring-interface-ring af-transition-[margin,opacity] af-duration-200 af-ease-linear focus-visible:af-ring-2 [&>svg]:af-size-4 [&>svg]:af-shrink-0",
        "group-data-[collapsible=icon]:af--mt-8 group-data-[collapsible=icon]:af-opacity-0",
        className,
      )}
      {...props}
    />
  );
};
SidebarGroupLabel.displayName = "SidebarGroupLabel";

const SidebarGroupAction = ({
  className,
  asChild = false,
  ...props
}: React.ComponentProps<"button"> & { asChild?: boolean }) => {
  const Comp = asChild ? Slot : "button";

  return (
    <Comp
      data-slot="sidebar-group-action"
      data-sidebar="group-action"
      className={cn(
        "af-absolute af-right-3 af-top-3.5 af-flex af-aspect-square af-w-5 af-items-center af-justify-center af-rounded-md af-p-0 af-text-interface-foreground af-outline-none af-ring-interface-ring af-transition-transform hover:af-bg-interface-accent hover:af-text-interface-accent-foreground focus-visible:af-ring-2 [&>svg]:af-size-4 [&>svg]:af-shrink-0",
        // Increases the hit area of the button on mobile.
        "after:af-absolute after:af--inset-2 after:md:af-hidden",
        "group-data-[collapsible=icon]:af-hidden",
        className,
      )}
      {...props}
    />
  );
};
SidebarGroupAction.displayName = "SidebarGroupAction";

const SidebarGroupContent = ({
  className,
  ...props
}: React.ComponentProps<"div">) => (
  <div
    data-slot="sidebar-group-content"
    data-sidebar="group-content"
    className={cn("af-w-full af-text-sm", className)}
    {...props}
  />
);
SidebarGroupContent.displayName = "SidebarGroupContent";

const SidebarMenu = ({ className, ...props }: React.ComponentProps<"ul">) => (
  <ul
    data-slot="sidebar-menu"
    data-sidebar="menu"
    className={cn(
      "af-flex af-w-full af-min-w-0 af-flex-col af-gap-1",
      className,
    )}
    {...props}
  />
);
SidebarMenu.displayName = "SidebarMenu";

const SidebarMenuItem = ({
  className,
  ...props
}: React.ComponentProps<"li">) => (
  <li
    data-slot="sidebar-menu-item"
    data-sidebar="menu-item"
    className={cn("af-group/menu-item af-relative", className)}
    {...props}
  />
);
SidebarMenuItem.displayName = "SidebarMenuItem";

const sidebarMenuButtonVariants = cva(
  "af-peer/menu-button af-group-has-[[data-sidebar=menu-action]]/menu-item:pr-8 group-data-[collapsible=icon]:af-!size-8 af-flex af-w-full af-items-center af-gap-2 af-overflow-hidden af-rounded-md af-p-2 af-text-left af-text-sm af-font-normal af-outline-none af-ring-interface-ring af-transition-[width,height,padding] hover:af-bg-interface-accent hover:af-text-interface-accent-foreground focus-visible:af-ring-2 active:af-bg-interface-accent active:af-text-interface-accent-foreground disabled:af-pointer-events-none disabled:af-opacity-50 aria-disabled:af-pointer-events-none aria-disabled:af-opacity-50 data-[active=true]:af-bg-interface-accent data-[active=true]:af-font-medium data-[active=true]:af-text-interface-accent-foreground data-[state=open]:hover:af-bg-interface-accent data-[state=open]:hover:af-text-interface-accent-foreground group-data-[collapsible=icon]:af-p-2 [&>span:last-child]:af-truncate [&>svg]:af-size-4 [&>svg]:af-shrink-0",
  {
    variants: {
      variant: {
        default:
          "hover:af-bg-interface-accent hover:af-text-interface-accent-foreground",
        outline:
          "af-bg-background af-shadow-[0_0_0_1px_hsl(var(--interface-border))] hover:af-bg-interface-accent hover:af-text-interface-accent-foreground hover:af-shadow-[0_0_0_1px_hsl(var(--interface-accent))]",
      },
      size: {
        default: "af-h-8 af-text-sm",
        sm: "af-h-7 af-text-xs",
        lg: "af-text-sm group-data-[collapsible=icon]:af-p-0",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

const SidebarMenuButton = ({
  asChild = false,
  isActive = false,
  variant = "default",
  size = "default",
  tooltip,
  className,
  ...props
}: React.ComponentProps<"button"> & {
  asChild?: boolean;
  isActive?: boolean;
  tooltip?: string | React.ComponentProps<typeof TooltipContent>;
} & VariantProps<typeof sidebarMenuButtonVariants>) => {
  const Comp = asChild ? Slot : "button";
  const { isMobile, state } = useSidebar();

  const button = (
    <Comp
      data-slot="sidebar-menu-button"
      data-sidebar="menu-button"
      data-size={size}
      data-active={isActive}
      className={cn(sidebarMenuButtonVariants({ variant, size }), className)}
      {...props}
    />
  );

  if (!tooltip) {
    return button;
  }

  if (typeof tooltip === "string") {
    tooltip = {
      children: tooltip,
    };
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>{button}</TooltipTrigger>
      <TooltipContent
        side="right"
        align="center"
        hidden={state !== "collapsed" || isMobile}
        {...tooltip}
      />
    </Tooltip>
  );
};
SidebarMenuButton.displayName = "SidebarMenuButton";

const SidebarMenuAction = ({
  className,
  asChild = false,
  showOnHover = false,
  ...props
}: React.ComponentProps<"button"> & {
  asChild?: boolean;
  showOnHover?: boolean;
}) => {
  const Comp = asChild ? Slot : "button";

  return (
    <Comp
      data-slot="sidebar-menu-action"
      data-sidebar="menu-action"
      className={cn(
        "af-peer-hover/menu-button:af-text-interface-accent-foreground af-absolute af-right-1 af-top-1.5 af-flex af-aspect-square af-w-5 af-items-center af-justify-center af-rounded-md af-p-0 af-text-interface-foreground af-outline-none af-ring-interface-ring af-transition-transform hover:af-bg-interface-accent hover:af-text-interface-accent-foreground focus-visible:af-ring-2 [&>svg]:af-size-4 [&>svg]:af-shrink-0",
        // Increases the hit area of the button on mobile.
        "after:af-absolute after:af--inset-2 after:md:af-hidden",
        showOnHover &&
          "af-opacity-0 af-transition-opacity group-hover/menu-item:af-opacity-100",
        className,
      )}
      {...props}
    />
  );
};
SidebarMenuAction.displayName = "SidebarMenuAction";

const SidebarMenuBadge = ({
  className,
  ...props
}: React.ComponentProps<"div">) => (
  <div
    data-slot="sidebar-menu-badge"
    data-sidebar="menu-badge"
    className={cn(
      "af-pointer-events-none af-absolute af-right-1 af-flex af-h-5 af-min-w-5 af-select-none af-items-center af-justify-center af-rounded-md af-px-1 af-text-xs af-font-medium af-tabular-nums af-text-interface-foreground",
      "af-peer-hover/menu-button:af-text-interface-accent-foreground af-peer-data-[active=true]/menu-button:af-text-interface-accent-foreground af-top-1/2 -af-translate-y-1/2 af-bg-primary af-text-interface",
      "group-data-[collapsible=icon]:af-right-1.5 group-data-[collapsible=icon]:af-top-1.5 group-data-[collapsible=icon]:af-h-2 group-data-[collapsible=icon]:af-w-2 group-data-[collapsible=icon]:af-min-w-2 group-data-[collapsible=icon]:af-translate-y-0 group-data-[collapsible=icon]:af-rounded-full group-data-[collapsible=icon]:af-bg-primary group-data-[collapsible=icon]:af-text-transparent",
      className,
    )}
    {...props}
  />
);
SidebarMenuBadge.displayName = "SidebarMenuBadge";

const SidebarMenuSkeleton = ({
  className,
  showIcon = false,
  ...props
}: React.ComponentProps<"div"> & {
  showIcon?: boolean;
}) => {
  // Random width between 50 to 90%.
  const width = React.useMemo(() => {
    return `${Math.floor(Math.random() * 40) + 50}%`;
  }, []);

  return (
    <div
      data-slot="sidebar-menu-skeleton"
      data-sidebar="menu-skeleton"
      className={cn(
        "af-flex af-h-8 af-items-center af-gap-2 af-rounded-md af-px-2",
        className,
      )}
      {...props}
    >
      {showIcon && (
        <Skeleton
          className="af-size-4 af-rounded-md af-bg-interface-background"
          data-sidebar="menu-skeleton-icon"
        />
      )}
      <Skeleton
        className="af-h-4 af-max-w-[--skeleton-width] af-flex-1 af-bg-interface-background"
        data-sidebar="menu-skeleton-text"
        style={
          {
            "--skeleton-width": width,
          } as React.CSSProperties
        }
      />
    </div>
  );
};
SidebarMenuSkeleton.displayName = "SidebarMenuSkeleton";

const SidebarMenuSub = ({
  className,
  ...props
}: React.ComponentProps<"ul">) => (
  <ul
    data-slot="sidebar-menu-sub"
    data-sidebar="menu-sub"
    className={cn(
      "af-mx-3.5 af-flex af-min-w-0 af-translate-x-px af-flex-col af-gap-1 af-border-l af-border-interface-border af-px-2.5 af-py-0.5",
      "group-data-[collapsible=icon]:af-hidden",
      className,
    )}
    {...props}
  />
);
SidebarMenuSub.displayName = "SidebarMenuSub";

const SidebarMenuSubItem = ({
  className,
  ...props
}: React.ComponentProps<"li">) => (
  <li
    data-slot="sidebar-menu-sub-item"
    data-sidebar="menu-sub-item"
    className={cn("af-group/menu-sub-item af-relative", className)}
    {...props}
  />
);
SidebarMenuSubItem.displayName = "SidebarMenuSubItem";

const SidebarMenuSubButton = ({
  asChild = false,
  size = "md",
  isActive,
  className,
  ...props
}: React.ComponentProps<"a"> & {
  asChild?: boolean;
  size?: "sm" | "md";
  isActive?: boolean;
}) => {
  const Comp = asChild ? Slot : "a";

  return (
    <Comp
      data-slot="sidebar-menu-sub-button"
      data-sidebar="menu-sub-button"
      data-size={size}
      data-active={isActive}
      className={cn(
        "af-flex af-h-7 af-min-w-0 af--translate-x-px af-items-center af-gap-2 af-overflow-hidden af-rounded-md af-px-2 af-text-interface-foreground af-outline-none af-ring-interface-ring hover:af-bg-interface-accent hover:af-text-interface-accent-foreground focus-visible:af-ring-2 active:af-bg-interface-accent active:af-text-interface-accent-foreground disabled:af-pointer-events-none disabled:af-opacity-50 aria-disabled:af-pointer-events-none aria-disabled:af-opacity-50 [&>span:last-child]:af-truncate [&>svg]:af-size-4 [&>svg]:af-shrink-0 [&>svg]:af-text-interface-accent-foreground",
        "data-[active=true]:af-bg-interface-accent data-[active=true]:af-text-interface-accent-foreground",
        {
          "af-text-xs": size === "sm",
          "af-text-sm": size === "md",
        },
        "group-data-[collapsible=icon]:af-hidden",
        className,
      )}
      {...props}
    />
  );
};
SidebarMenuSubButton.displayName = "SidebarMenuSubButton";

export {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupAction,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInput,
  SidebarInset,
  SidebarMenu,
  SidebarMenuAction,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSkeleton,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarProvider,
  SidebarRail,
  SidebarSeparator,
  SidebarTrigger,
  SidebarWrapper,
  useSidebar,
};
